import type { StructuredContentRoot } from "@repo/app-types/content/structured/types";
import type { CoachingGradingValueType } from "@repo/app-types/entities/coachingScore";
import type { DateRange } from "@repo/app-types/entities/dateRange";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useEffect, useState, type FC, type ReactNode } from "react";
import type { StructuredContentRenderContext } from "../structuredContent/structuredContentRenderer.js";
import { CoachingGoalBodyPreview } from "./coachingGoalBodyPreview.js";
import { CoachingGoalHeader } from "./coachingGoalHeader.js";
import { CoachingGoalSummaryPanel } from "./coachingGoalSummaryPanel.js";
import type {
  CoachingGoalCallPoint,
  CoachingGoalStatusTone,
} from "./coachingGoalTypes.js";

export interface WeeklyCoachingGoalCardProps {
  dateRange: DateRange;
  /** The coaching-period selector, rendered in the card header. */
  periodSelector?: ReactNode;
  criterionName: string;
  /** Grading context / description shown in the criterion info popover. */
  criterionInfo?: string;
  impactLabel: string;
  startedScore: number;
  targetScore: number;
  gradingValueType?: CoachingGradingValueType;
  /**
   * Running period average after each call, oldest first — one entry per
   * call in {@link calls}. Empty when the period has no calls yet.
   */
  scorePath: readonly number[];
  /** Fraction (0-1) of the goal period elapsed. */
  dayProgress: number;
  /** Short encouragement copy shown in the trend meter's score badge. */
  statusLabel: string;
  statusTone: CoachingGoalStatusTone;
  /**
   * Optional manager note (or its editor), rendered between the summary panel
   * and the AI coaching note body.
   */
  managerNoteSlot?: ReactNode;
  /** The coaching note body (V3 AST); headings render as section labels. */
  content: StructuredContentRoot;
  /** Render context so grading references/quotes in the body are interactive. */
  renderContext?: StructuredContentRenderContext;
  calls: readonly CoachingGoalCallPoint[];
  /** Renders a chart skeleton while the calls load, avoiding a layout jump. */
  isChartLoading?: boolean;
  isExpanded?: boolean;
  /** Keeps the note body expanded and hides its collapse toggle (e.g. while the manager note editor is open). */
  isExpansionForced?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  activeCallId?: string;
  onCallOpen?: (gradingId: GradingId) => void;
}

export const WeeklyCoachingGoalCard: FC<WeeklyCoachingGoalCardProps> = (
  props,
) => {
  const {
    dateRange,
    periodSelector,
    criterionName,
    criterionInfo,
    impactLabel,
    startedScore,
    targetScore,
    gradingValueType = "1_5_score",
    scorePath,
    dayProgress,
    statusLabel,
    statusTone,
    managerNoteSlot,
    content,
    renderContext,
    calls,
    isChartLoading,
    isExpanded = false,
    isExpansionForced,
    onExpandedChange,
    activeCallId,
    onCallOpen,
  } = props;

  const [isBodyExpanded, setIsBodyExpanded] = useState(isExpanded);

  useEffect(() => {
    setIsBodyExpanded(isExpanded);
  }, [isExpanded]);

  const handleExpandedChange = (nextIsExpanded: boolean) => {
    setIsBodyExpanded(nextIsExpanded);
    onExpandedChange?.(nextIsExpanded);
  };

  return (
    // `isolate` scopes the card's internal z-indexes (the note body's
    // "Continue reading" overlay) to their own stacking context, so they stack
    // among themselves without painting over page chrome that scrolls behind
    // the card, like the sticky `PageHeader`.
    <section className="relative isolate flex flex-col gap-y-4 rounded-lg border border-border-light bg-card p-4 shadow-xs">
      <CoachingGoalHeader
        dateRange={dateRange}
        periodSelector={periodSelector}
      />

      <CoachingGoalSummaryPanel
        criterionName={criterionName}
        criterionInfo={criterionInfo}
        impactLabel={impactLabel}
        startedScore={startedScore}
        targetScore={targetScore}
        gradingValueType={gradingValueType}
        scorePath={scorePath}
        dayProgress={dayProgress}
        statusLabel={statusLabel}
        statusTone={statusTone}
        calls={calls}
        activeCallId={activeCallId}
        onCallOpen={onCallOpen}
        isChartLoading={isChartLoading}
      />

      {managerNoteSlot}

      <CoachingGoalBodyPreview
        content={content}
        renderContext={renderContext}
        isExpanded={isBodyExpanded}
        isExpansionForced={isExpansionForced}
        onExpandedChange={handleExpandedChange}
      />
    </section>
  );
};
